import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query';
import apiSlice from '..';
import { BuyerStaticInfo, SellerStaticInfo } from '../../../components/CustomerInfoPopup/types';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { User } from '../../../types/user';
import { EntityWithCount } from '../types';

export type Contact = {
  id: number;
  name: string;
  email: string;
  job_title: string;
  phone: string;
};

type LastSoldVehicle = {
  id: string;
  vehicle_price: number;
  year: number;
  make: string;
  model: string;
  date_status: string;
  user: {
    id: number;
    dealership_name: string;
  };
  paymentMethodLabel: string;
};

export type BuyerInfo = {
  id: string;
  role: 'buyer';
  name: string;
  primary_contact: Contact;
  secondary_contact?: Contact;
  dealership_name: string;
  purchase_methods: string[];
  dealer_license_number: string;
  seller_permit_number?: string;
  address: {
    city: string;
    state: string;
    zip: string;
    street: string;
  };
  contacts: Contact[];
  lastSoldVehicles: LastSoldVehicle[];
  notes: string;
};

export type SellerInfo = {
  id: string;
  role: 'seller';
  name: string;
  dealership_name: string;
  primary_contact: Contact;
  secondary_contact?: Contact;
  fee: number;
  pickup_instruction: string;
  purchase_options: string[];
  dealer_license_number: string;
  address: {
    city: string;
    state: string;
    zip: string;
    street: string;
  };
  contacts: Contact[];
  lastSoldVehicles: LastSoldVehicle[];
  notes: string;
};

export type BuyerOptions = Array<{
  id: string;
  dealership_name: string;
}>;

export type SellerOptions = Array<{
  id: number;
  dealership_name: string;
}>;

export const accountsAdapter = createEntityAdapter<User>();

export const customersApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getHoverCustomerInfo: builder.query<BuyerInfo | SellerInfo, number>({
      query: customerId => ({
        url: `/admin/customers/${customerId}/hover-info`,
        method: 'GET'
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getBuyerOptions: builder.query<BuyerOptions, void>({
      query: () => ({
        url: '/users/buyers',
        method: 'GET'
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getSellerOptions: builder.query<SellerOptions, void>({
      query: () => ({
        url: '/users/sellers',
        method: 'GET'
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    adminLinkAccount: builder.mutation<void, { linkEmail: string | null; customerId: number }>({
      query: ({ customerId, linkEmail }) => ({
        url: `/admin/customers/${customerId}/link-account`,
        method: 'POST',
        body: { linkEmail }
      })
    }),
    getLinkInfoByEmail: builder.query<any, { customerId: number; email: string }>({
      query: ({ customerId, email }) => ({
        url: `/admin/customers/${customerId}/link`,
        method: 'GET',
        params: { email }
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getAdditionalCustomerInfo: builder.query<BuyerStaticInfo | SellerStaticInfo, number>({
      query: customerId => ({
        url: `/admin/customers/${customerId}/additional-info`,
        method: 'GET'
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getAccounts: builder.query<EntityWithCount<User>, { limit: number; offset: number } & Record<string, any>>({
      query: ({ limit, offset, ...filters }) => ({
        url: `/admin/accounts`,
        method: 'GET',
        params: {
          limit,
          offset,
          ...filters
        }
      }),
      transformResponse: (response: any) => {
        return {
          rows: accountsAdapter.addMany(accountsAdapter.getInitialState(), response.data.rows),
          count: Number(response.data.count)
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { offset, ...restArgs } = queryArgs;
        return defaultSerializeQueryArgs({ queryArgs: restArgs, endpointDefinition, endpointName });
      },
      merge: (currentCache, newItems, { arg, baseQueryMeta }) => {
        const newUsers = (newItems.rows?.ids?.map(el => newItems.rows.entities[el]) ?? []) as any[];
        currentCache.count = newItems.count;
        if (arg.offset === 1) {
          accountsAdapter.setAll(currentCache.rows, newUsers);
        } else {
          accountsAdapter.addMany(currentCache.rows, newUsers);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.offset !== previousArg?.offset;
      }
    })
  }),
  overrideExisting: true
});

export const {
  useGetHoverCustomerInfoQuery,
  useGetBuyerOptionsQuery,
  useGetSellerOptionsQuery,
  useAdminLinkAccountMutation,
  useGetLinkInfoByEmailQuery,
  useGetAdditionalCustomerInfoQuery,
  useGetAccountsQuery
} = customersApi;
export default customersApi;
